/* Production cost per kilo of yarn, as the Production design works it out.
 *
 *   cotton  the candy rate over the kilos in a candy, over the realization
 *   power   EB and solar units at their own rates, over the kilos made on
 *           the days the meters were read — a blank meter is not a day of
 *           zero draw, so it is left out of both sides
 *   other   everything else — wages, maintenance, packing, interest,
 *           depreciation — as a monthly sum spread over the days, and
 *           carried by one unit in proportion to its share of output
 *
 * An input is blank until the mill supplies it. The design shipped with
 * guesses (₹62,000 a candy, 85% realization, ₹7.50 and ₹3 a unit, ₹2.4 Cr a
 * month) and a cost built on guesses reads as a fact on a tile, so a rupee
 * figure that needs a missing input shows as "–" and says what it is
 * waiting for. The power units themselves are real readings and are shown
 * regardless. Fill a value in here and everything that depends on it
 * appears.
 *
 * The power tariffs are now the mill's own, so power cost per kg is live.
 * The cotton rate, realization and overhead are still blank, which is why
 * the cost *total* still shows as "–".
 *
 * One departure: the design divides the candy rate by 355.62 kg. This app
 * uses 356 kg to a candy everywhere (see README, "One number that changed"),
 * so it does here too; it moves the cotton cost by about 0.1%.
 */

export const COST_INPUTS: {
  /** Cotton, ₹ per candy. */
  candyRate: number | null
  /** Yarn realized from cotton, %. */
  realization: number | null
  /** EB tariff, ₹ per unit. */
  ebTariff: number | null
  /** Solar cost, ₹ per unit. */
  solarCost: number | null
  /** Other conversion overhead, ₹ per month. */
  otherOverhead: number | null
} = {
  candyRate: null,
  realization: null,
  // The mill's own figures, November 2026. EB is the landed tariff, after
  // taxes. Solar is zero on purpose: the panels are paid for, so a solar
  // unit costs nothing to draw and simply displaces a ₹7.50 grid unit. That
  // reads the saving at its largest — it carries no capex or O&M — so the
  // power figure is a cash cost per kilo, not a cost of ownership.
  ebTariff: 7.5,
  solarCost: 0,
  otherOverhead: null,
}

export const CANDY_KG = 356

export interface CostPerKg {
  /** True when at least one day had a meter reading; `ukg` is null
   *  otherwise. */
  metered: boolean
  /** Units of power per kg of yarn, over the metered days. */
  ukg: number | null
  /** Each is null until the inputs it needs are set (and, for power, until
   *  a meter was read); `total` only once all three are known. */
  cotton: number | null
  power: number | null
  other: number | null
  total: number | null
  /** What is still blank, in the words a tile can show. */
  missing: string[]
}

/** The inputs a figure is waiting for, by name; empty when it can be made. */
export function missingInputs(): string[] {
  const c = COST_INPUTS
  const out: string[] = []
  if (c.candyRate == null) out.push('cotton rate')
  if (c.realization == null) out.push('realization')
  if (c.ebTariff == null || c.solarCost == null) out.push('power tariffs')
  if (c.otherOverhead == null) out.push('overhead')
  return out
}

/** The cotton in a candy, per kilo of lint; null without a candy rate. */
export const lintPerKg = () =>
  COST_INPUTS.candyRate == null ? null : COST_INPUTS.candyRate / CANDY_KG

/** Cotton per kilo of yarn, before anything is spent converting it. */
export const cottonPerKg = () =>
  COST_INPUTS.candyRate == null || !COST_INPUTS.realization
    ? null
    : COST_INPUTS.candyRate / CANDY_KG / (COST_INPUTS.realization / 100)

/** `meteredKg` is the output on the days the meters were read; `kg` and
 *  `days` are the whole period the overhead is spread over; `share` is the
 *  unit's share of the plant's output (1 for the plant). null when there
 *  is no output to divide by. */
export function costPerKg(ebUnits: number, solarUnits: number, meteredKg: number,
  kg: number, days: number, share = 1): CostPerKg | null {
  if (!kg) return null
  const c = COST_INPUTS
  const metered = meteredKg > 0 && ebUnits + solarUnits > 0
  const cotton = cottonPerKg()
  const power = metered && c.ebTariff != null && c.solarCost != null
    ? (ebUnits * c.ebTariff + solarUnits * c.solarCost) / meteredKg : null
  const other = c.otherOverhead == null ? null : (c.otherOverhead / 30) * days * share / kg
  return {
    metered, cotton, power, other,
    ukg: metered ? (ebUnits + solarUnits) / meteredKg : null,
    total: cotton != null && power != null && other != null ? cotton + power + other : null,
    missing: missingInputs(),
  }
}
